type RawId = { value?: string | null } | string | null | undefined;

export type GenerateIdsFieldKeyOptions = {
  prefix?: string;
  emptyKey?: string;
  firstSnippetLength?: number;
  lastSnippetLength?: number;
};

const HASH_BASE = 31;
const HASH_MOD = 2 ** 31 - 1;
const DEFAULT_SNIPPET_LENGTH = 8;
const DEFAULT_PREFIX = 'ids-field';
const DEFAULT_EMPTY_TOKEN = 'empty';

const toStringOrUndefined = (rawId: RawId): string | undefined => {
  if (typeof rawId === 'string') return rawId;
  if (rawId && typeof rawId === 'object' && 'value' in rawId) {
    return typeof rawId.value === 'string' ? rawId.value : undefined;
  }
  return undefined;
};

const getIdsHashKey = (ids: string[]) => {
  let hash = 0;
  ids.forEach((id) => {
    for (let i = 0; i < id.length; i += 1) {
      hash = (hash * HASH_BASE + id.charCodeAt(i)) % HASH_MOD;
    }
  });
  return hash.toString(36);
};

export default function generateIdsFieldKey(
  rawIds: RawId[] | undefined,
  options: GenerateIdsFieldKeyOptions = {},
) {
  const prefix = options.prefix ?? DEFAULT_PREFIX;
  const emptyKey = options.emptyKey ?? `${prefix}-${DEFAULT_EMPTY_TOKEN}`;
  const firstSnippetLength =
    options.firstSnippetLength ?? DEFAULT_SNIPPET_LENGTH;
  const lastSnippetLength = options.lastSnippetLength ?? DEFAULT_SNIPPET_LENGTH;

  const normalizedIds = (rawIds ?? [])
    .map(toStringOrUndefined)
    .filter((id): id is string => Boolean(id));

  if (!normalizedIds.length) {
    return emptyKey;
  }

  const uniqueSortedIds = Array.from(new Set(normalizedIds)).sort();
  const hashPart = getIdsHashKey(uniqueSortedIds);
  const firstId =
    uniqueSortedIds[0]?.slice(0, firstSnippetLength) ?? DEFAULT_EMPTY_TOKEN;
  const lastId =
    uniqueSortedIds[uniqueSortedIds.length - 1]?.slice(-lastSnippetLength) ??
    DEFAULT_EMPTY_TOKEN;

  return `${prefix}-${uniqueSortedIds.length}-${firstId}-${lastId}-${hashPart}`;
}
